import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { useHighlightStore } from "../../stores/highlightStore";
import "../../styles/MainContent.css";
import {
  processContentWithBlocks,
  validateBlockStructure,
} from "../../utils/blockProcessor";
import { extractBodyContent, isCompleteHtmlDocument } from "../../utils/helpers";
import { createHighlighter } from "../../utils/webHighlightUtils";
import { LoadingSpinner } from "../ui";

const MainContent = memo(({ topic, textSize, courseData, onPanelClose, targetHighlightId, onScrollComplete }) => {
  // Refs for highlighting
  const contentRef = useRef(null);
  const highlighterRef = useRef(null);
  const lastSyncedHighlights = useRef(new Set());

  // Highlighting store
  const addHighlight = useHighlightStore((state) => state.addHighlight);
  const getHighlights = useHighlightStore((state) => state.getHighlights);
  const fetchHighlights = useHighlightStore((state) => state.fetchHighlights);
  const setHighlighter = useHighlightStore((state) => state.setHighlighter);
  const setLoading = useHighlightStore((state) => state.setLoading);
  const setError = useHighlightStore((state) => state.setError);
  const deleteHighlight = useHighlightStore((state) => state.delete);

  // Subscribe to highlights state for DOM synchronization
  const highlights = useHighlightStore((state) => state.highlights);

  // Error handling for component-level errors
  const { addError } = useErrorHandler();

  // Simple hash function for content identification
  const createSimpleHash = useCallback((str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).substring(0, 8);
  }, []);

  // Helper function to extract resourceId and courseId
  const getCurrentResourceInfo = useCallback((topic) => {
    if (!topic) {
      return { resourceId: null, courseId: null, error: 'Topic is missing' };
    }
    let courseId = courseData?.id || topic.courseId || topic.originalStructure?.courseId || null;
    if (!courseId) {
      const urlParams = new URLSearchParams(window.location.search);
      courseId = urlParams.get('courseId');
    }
    let resourceId = topic.resourceId || topic.originalStructure?.resourceId || null;
    if (!resourceId && topic.resources && topic.resources.length > 0) {
      const currentPageIndex = topic.currentPageIndex || 0;
      const currentResource = topic.resources[currentPageIndex];
      resourceId = currentResource?.id || null;
    }
    if (!resourceId) {
      resourceId = topic.id || topic.realTopicId || null;
    }
    console.log('🔍 getCurrentResourceInfo:', { courseId, resourceId });
    if (!resourceId || !courseId) {
      return {
        resourceId,
        courseId,
        error: `Missing required fields: ${!resourceId ? 'resourceId' : ''} ${!courseId ? 'courseId' : ''}`.trim()
      };
    }
    return { resourceId, courseId, error: null };
  }, [courseData]);

  // Create robust page ID
  const createRobustPageId = useCallback((topic) => {
    if (!topic) return "default";
    const baseId = topic.id || "unknown";
    const realTopicId = topic.realTopicId || "unknown";
    const { resourceId } = getCurrentResourceInfo(topic);
    const originalStructure = topic.originalStructure || {};
    const pageNumber = originalStructure.pageNumber || 1;
    const subTopicId = originalStructure.subTopicId || "unknown";
    const resourcePart = resourceId || "no-resource";
    return `${baseId}__${realTopicId}__${resourcePart}__${subTopicId}__page${pageNumber}`;
  }, [getCurrentResourceInfo]);

  // Get current selected color
  const getCurrentColor = useCallback(() => {
    return useHighlightStore.getState().selectedColor;
  }, []);

  // Sync highlights with DOM
  const syncHighlightsWithDOM = useCallback((topicId, pageId, forceSync = false) => {
    if (!highlighterRef.current || !contentRef.current) {
      console.warn('⚠️ Cannot sync highlights - missing highlighter or content ref');
      return;
    }
    const currentPageHighlights = getHighlights(topicId, pageId);
    const currentHighlightFrontendIds = new Set(currentPageHighlights.map(h => h.highlightData?.id).filter(Boolean));
    const lastSynced = lastSyncedHighlights.current;
    const hasChanges = forceSync ||
      currentHighlightFrontendIds.size !== lastSynced.size ||
      [...currentHighlightFrontendIds].some(id => !lastSynced.has(id)) ||
      [...lastSynced].some(id => !currentHighlightFrontendIds.has(id));
    if (!hasChanges) {
      console.log('🔄 No highlight changes detected, skipping DOM sync');
      return;
    }
    console.log(`🔄 Syncing highlights: ${currentPageHighlights.length} for Topic: ${topicId}, Page: ${pageId}`);
    try {
      highlighterRef.current.removeAll();
      if (currentPageHighlights.length > 0) {
        console.log(`🔄 Restoring ${currentPageHighlights.length} highlights to DOM`);
        highlighterRef.current.deserialize(currentPageHighlights);
      }
      lastSyncedHighlights.current = new Set(currentHighlightFrontendIds);
      console.log('✅ Highlight DOM sync completed');
    } catch (error) {
      console.error('❌ Failed to sync highlights with DOM:', error);
      setError("Failed to sync highlights");
    }
  }, [getHighlights, setError]);

  // Handle panel close to force sync
  useEffect(() => {
    if (onPanelClose && topic?.id) {
      const topicId = topic.realTopicId || topic.id;
      const pageId = createRobustPageId(topic);
      onPanelClose(() => syncHighlightsWithDOM(topicId, pageId, true));
    }
  }, [onPanelClose, topic?.id, topic?.realTopicId, createRobustPageId, syncHighlightsWithDOM]);

  // Handle scrolling to target highlight
  useEffect(() => {
    if (!targetHighlightId || !contentRef.current) return;

    // Function to find and scroll to highlight
    const findAndScrollToHighlight = (attempts = 0) => {
      const maxAttempts = 10; // Increased from 5 to 10
      const highlightElements = contentRef.current.querySelectorAll(`[data-highlight-id="${targetHighlightId}"]`);

      if (highlightElements && highlightElements.length > 0) {
        console.log(`✅ Found target highlight ${targetHighlightId}, scrolling...`);
        
        // Scroll to the highlight
        highlightElements[0].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });

        // Add focus class
        highlightElements.forEach(el => {
          el.classList.add('highlight-focus');
          setTimeout(() => el.classList.remove('highlight-focus'), 3000);
        });

        // Notify parent that scroll is complete
        if (onScrollComplete) {
          onScrollComplete();
        }
        return;
      }

      // If not found and we haven't reached max attempts, try again
      if (attempts < maxAttempts) {
        const nextDelay = Math.min(100 * Math.pow(2, attempts), 1000); // Exponential backoff, max 1s
        console.log(`⏳ Target highlight ${targetHighlightId} not found, retrying... (${attempts + 1}/${maxAttempts})`);
        setTimeout(() => findAndScrollToHighlight(attempts + 1), nextDelay);
        return;
      }

      // If we've exhausted all attempts, try one last time after ensuring highlights are synced
      if (topic?.id) {
        console.log(`🔍 Final attempt: Syncing highlights before last try for ${targetHighlightId}`);
        const topicId = topic.realTopicId || topic.id;
        const pageId = createRobustPageId(topic);
        syncHighlightsWithDOM(topicId, pageId, true);
        
        // One final attempt after syncing
        setTimeout(() => {
          const finalElements = contentRef.current.querySelectorAll(`[data-highlight-id="${targetHighlightId}"]`);
          if (finalElements.length > 0) {
            console.log(`✅ Found highlight ${targetHighlightId} after final sync, scrolling...`);
            finalElements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
            finalElements.forEach(el => {
              el.classList.add('highlight-focus');
              setTimeout(() => el.classList.remove('highlight-focus'), 3000);
            });
            if (onScrollComplete) onScrollComplete();
          } else {
            console.warn(`⚠️ Target highlight ${targetHighlightId} not found after ${maxAttempts} attempts and final sync`);
          }
        }, 300); // Give some time for the DOM to update
      } else {
        console.warn(`⚠️ Target highlight ${targetHighlightId} not found after ${maxAttempts} attempts`);
      }
    };

    // Start the process
    findAndScrollToHighlight();
  }, [targetHighlightId, highlights, onScrollComplete, topic, createRobustPageId, syncHighlightsWithDOM]);

  // Highlighting event handlers
  const handleHighlightClick = useCallback((highlightData) => {
    console.log("Highlight clicked:", highlightData.id);
  }, []);

  const handleSelectionCreate = useCallback(
    async (data, inst) => {
      if (!topic?.id) {
        console.warn("⚠️ Cannot create highlight: Topic ID is missing.");
        return;
      }
      const { resourceId, courseId, error } = getCurrentResourceInfo(topic);
      if (error) {
        console.error(`⚠️ Cannot create highlight: ${error}`);
        setError(`Cannot create highlight: ${error}`);
        return;
      }
      try {
        setLoading(true);
        const topicId = topic.realTopicId || topic.id;
        const pageId = createRobustPageId(topic);
        const sources = data.sources || [];
        if (sources.length === 0) {
          return;
        }
        const newHighlightBackendIds = [];
        for (const source of sources) {
          const highlightPayload = {
            ...source,
            topicId,
            pageId,
            createdAt: new Date().toISOString(),
          };
          try {
            const savedHighlight = await addHighlight(topicId, pageId, highlightPayload, resourceId, courseId);
            if (savedHighlight?.id) {
              newHighlightBackendIds.push(savedHighlight.id);
              console.log(`✅ Highlight saved: Backend ID: ${savedHighlight.id}, Frontend ID: ${savedHighlight.highlightData?.id}`);
            }
          } catch (error) {
            console.error("Failed to add highlight:", error);
          }
        }
        if (newHighlightBackendIds.length > 0) {
          setTimeout(() => {
            syncHighlightsWithDOM(topicId, pageId, true);
          }, 10);
        }
      } catch (error) {
        console.error("Failed to create highlight:", error);
        setError("Failed to create highlight");
        addError("Failed to create highlight");
      } finally {
        setLoading(false);
      }
    },
    [topic, addHighlight, setLoading, setError, addError, createRobustPageId, syncHighlightsWithDOM, getCurrentResourceInfo]
  );

  const handleHighlightRemove = useCallback(
    async (frontendHighlightIds) => {
      if (!Array.isArray(frontendHighlightIds) || frontendHighlightIds.length === 0) return;
      console.log(`🗑️ Removing ${frontendHighlightIds.length} highlights:`, frontendHighlightIds);

      try {
        // Wait for all highlight deletions to complete
        await Promise.all(
          frontendHighlightIds.map(async (frontendId) => {
            if (frontendId) {
              await deleteHighlight(frontendId);
            }
          })
        );

        // Sync DOM after all deletions are complete
        if (topic?.id) {
          const topicId = topic.realTopicId || topic.id;
          const pageId = createRobustPageId(topic);
          // Add a small delay to ensure store state is fully updated
          setTimeout(() => {
            syncHighlightsWithDOM(topicId, pageId, true);
          }, 100);
        }
      } catch (error) {
        console.error('❌ Error removing highlights:', error);
        setError('Failed to remove highlights');
      }
    },
    [deleteHighlight, topic, createRobustPageId, syncHighlightsWithDOM, setError]
  );

  // Initialize highlighter
  useEffect(() => {
    console.log("🔍 Highlighter useEffect triggered");
    if (!contentRef.current || !topic?.id) {
      console.warn("⚠️ Missing content ref or topic ID, skipping highlighter init");
      return;
    }
    if (highlighterRef.current) {
      console.log("🧹 Cleaning up previous highlighter");
      highlighterRef.current.stop();
      highlighterRef.current = null;
    }
    lastSyncedHighlights.current = new Set();
    if (contentRef.current) {
      const existingHighlightSpans = contentRef.current.querySelectorAll(".web-highlight-wrap, .rangy-highlight");
      existingHighlightSpans.forEach((span) => {
        const parent = span.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(span.textContent), span);
          parent.normalize();
        }
      });
    }
    const initializeHighlighter = async () => {
      try {
        const highlighter = await createHighlighter(
          contentRef.current,
          handleHighlightClick,
          handleSelectionCreate,
          getCurrentColor,
          handleHighlightRemove
        );
        if (highlighter) {
          highlighterRef.current = highlighter;
          setHighlighter(highlighter);
          highlighter.run();
          const topicId = topic.realTopicId || topic.id;
          const pageId = createRobustPageId(topic);
          try {
            await fetchHighlights(topicId, pageId);
            setTimeout(() => {
              syncHighlightsWithDOM(topicId, pageId, true);
            }, 100);
          } catch (error) {
            console.error("❌ Failed to fetch highlights from API:", error);
            setTimeout(() => {
              syncHighlightsWithDOM(topicId, pageId, true);
            }, 100);
          }
        }
      } catch (error) {
        console.error("❌ Failed to initialize highlighter:", error);
        setError("Failed to initialize highlighting");
      }
    };
    const timeoutId = setTimeout(initializeHighlighter, 100);
    return () => {
      clearTimeout(timeoutId);
      if (highlighterRef.current) {
        console.log("🧹 Cleaning up highlighter on unmount...");
        highlighterRef.current.stop();
        highlighterRef.current = null;
      }
      lastSyncedHighlights.current = new Set();
    };
  }, [
    topic?.id,
    topic?.realTopicId,
    topic?.originalStructure?.pageId,
    handleHighlightClick,
    handleSelectionCreate,
    handleHighlightRemove,
    setHighlighter,
    fetchHighlights,
    syncHighlightsWithDOM,
    createRobustPageId,
    getCurrentColor
  ]);

  // Content processing
  const getContentHtml = useCallback(() => {
    if (topic?.content && typeof topic.content === 'string') {
      console.log('🔄 Processing direct HTML content for topic:', topic.id);
      return isCompleteHtmlDocument(topic.content) ? extractBodyContent(topic.content) : topic.content;
    }
    if (!topic?.content?.body || topic.content.body.length === 0) {
      console.log('❌ No content body found');
      return "";
    }
    console.log("🔄 Processing structured content for topic:", topic.id);
    const topicId = topic.realTopicId || topic.id || "default";
    const pageId = createRobustPageId(topic);
    const processedContent = processContentWithBlocks(topic.content.body, topicId, pageId);
    validateBlockStructure(processedContent);
    return processedContent;
  }, [topic?.content, topic?.id, topic?.resources, topic?.currentPageIndex, createRobustPageId]);

  const contentHtml = useMemo(() => {
    if (topic?.resources !== undefined) {
      if (topic.resources.length === 0) {
        console.log('📄 Topic has empty resources array');
        return "__EMPTY_RESOURCES__";
      }
      const currentPageIndex = topic.currentPageIndex || 0;
      const currentResource = topic.resources[currentPageIndex];

      console.log('🔍 MainContent Debug:', {
        topicId: topic.id,
        currentPageIndex,
        resourcesLength: topic.resources.length,
        currentResource: currentResource ? { id: currentResource.id, hasContent: !!currentResource.content } : 'undefined'
      });

      if (currentResource && currentResource.content) {
        console.log(`📄 Rendering resource page ${currentPageIndex + 1}/${topic.resources.length}:`, currentResource.title);
        if (currentResource.resourceTypeName === 'HTML') {
          return extractBodyContent(currentResource.content);
        } else {
          return currentResource.content;
        }
      }
      console.warn('⚠️ Current resource or content is missing for page index:', currentPageIndex);
      return "";
    }
    return getContentHtml();
  }, [topic?.content, topic?.resources, topic?.currentPageIndex, topic?.id, createRobustPageId, getContentHtml]);

  if (!topic) {
    return (
      <main className="main-content-panel">
        <LoadingSpinner size="large" message="Loading content..." />
      </main>
    );
  }

  return (
    <main className={`main-content-panel text-size-${textSize}`}>
      <article className="lesson-content">
        <div className="lesson-content-body" ref={contentRef}>
          {contentHtml && contentHtml !== "__EMPTY_RESOURCES__" ? (
            <div
              className="html-content"
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          ) : (
            <div className="no-content">
              {contentHtml === "__EMPTY_RESOURCES__" ? (
                <p>No data available</p>
              ) : topic?.resources && topic.resources.length > 0 ? (
                <p>No content available - Resources found but content could not be displayed.</p>
              ) : (
                <p>No content available for this topic.</p>
              )}
            </div>
          )}
        </div>
      </article>
    </main>
  );
});

export default MainContent;